import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';

const TermsOfUse = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center space-x-2 mb-6">
              <TrendingUp className="w-8 h-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">Finance Vitals</span>
            </Link>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Finance Vitals – Terms of Use</h1>
            <p className="text-gray-600">Effective Date: January 1, 2026</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                These Terms of Use ("Terms") govern your access to and use of the Finance Vitals platform ("Finance Vitals", "we", "us", "our"). By accessing or using our website, services, or tools (collectively, the "Service"), you agree to these Terms. If you do not agree, you may not use the Service.
              </p>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Purpose of the Service</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Finance Vitals provides AI-powered financial analysis based on user-submitted financial documents and questionnaire responses. The analysis, health scores, and recommendations provided by the Service are for informational purposes only and are intended to offer preliminary insights—not comprehensive accounting, tax, legal, or financial advice.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  No client–accountant or fiduciary relationship is created through use of this Service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Not Financial Advice</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  You acknowledge and agree that:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Finance Vitals does not provide accounting, bookkeeping, tax, legal, audit, or financial advisory services.</li>
                  <li>All outputs are generated by artificial intelligence and may contain inaccuracies, omissions, or errors.</li>
                  <li>You should not rely solely on AI-generated insights when making financial or business decisions.</li>
                  <li>You are responsible for consulting qualified professionals before taking action based on any analysis.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. No Warranty</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The Service is provided on an "AS IS" and "AS AVAILABLE" basis without warranties of any kind, express or implied, including but not limited to accuracy, completeness, reliability, merchantability, fitness for a particular purpose, or non-infringement.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We do not guarantee uninterrupted, secure, or error-free operation of the Service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Limitation of Liability</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  To the maximum extent permitted by law:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Finance Vitals shall not be liable for any indirect, incidental, consequential, special, punitive, or exemplary damages.</li>
                  <li>Our total liability for any claim shall not exceed the amount you paid for the Service in the 12 months preceding the claim.</li>
                  <li>We are not liable for business losses, lost profits, lost opportunities, or regulatory outcomes resulting from reliance on the Service.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  Some jurisdictions do not allow certain limitations; where prohibited, limitations apply only to the extent permitted.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. User Conduct</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  You agree not to:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Upload files containing malware or harmful code</li>
                  <li>Attempt to gain unauthorized access to the Service</li>
                  <li>Reverse engineer, interfere with, or disrupt the platform</li>
                  <li>Use the Service for fraudulent or illegal purposes</li>
                  <li>Attempt to access data belonging to other users</li>
                  <li>Share your login credentials with any unauthorized party</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  Violation of these terms may result in suspension or termination of your account.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Uploaded Files & Data Handling</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  You may upload financial documents for analysis. You acknowledge that:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Files may be temporarily stored for processing</li>
                  <li>We use industry-standard measures to protect stored data</li>
                  <li>No system is perfectly secure, and you upload files at your own risk</li>
                  <li>We do not retain financial documents longer than necessary to provide the Service, unless required for security, quality assurance, or compliance</li>
                  <li>We may use third-party AI processors (e.g., OpenAI) to analyze uploaded data</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  For full data practices, please review our Privacy Policy.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Payments, Fees, and Refunds</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Finance Vitals offers paid services, including the Deep Analysis product.
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>All prices are displayed before checkout</li>
                  <li>Payments are processed through a secure third-party provider (e.g., Stripe)</li>
                  <li>You authorize us to charge your selected payment method for applicable fees</li>
                  <li>All sales are final. Refunds are provided only where a verified technical issue prevented you from accessing or receiving the service you purchased</li>
                  <li>We do not provide refunds based on dissatisfaction with the analysis, its conclusions, or its results</li>
                  <li>Nothing in this section limits any mandatory rights you may have under applicable consumer protection law</li>
                  <li>Chargebacks may result in account suspension</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Account Termination</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  We may suspend or terminate your account if:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>You violate these Terms</li>
                  <li>Your payment fails or is reversed</li>
                  <li>You engage in abuse, fraud, or misuse of the platform</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  Upon termination, we may delete your data except where retention is required for legal, security, or compliance purposes.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Professionals & Third-Party Services</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Finance Vitals may facilitate connections between business owners and accounting or bookkeeping professionals. We do not:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li>Endorse or verify the qualifications of any professional</li>
                  <li>Guarantee the accuracy or quality of any services provided by third parties</li>
                  <li>Assume responsibility for disputes between users and professionals</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  Any relationship formed is solely between the parties involved.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Intellectual Property</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The platform, including text, UI design, algorithms, reports, scoring models, and proprietary prompts, is owned by Finance Vitals.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Users retain ownership of the documents they upload.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Finance Vitals retains rights to AI-generated insights solely for delivering the Service to the user.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  You may not copy, distribute, resell, or reverse engineer the platform or its outputs.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Modifications to the Terms</h2>
                <p className="text-gray-700 leading-relaxed">
                  We may update these Terms at any time. Continued use of the Service after changes implies acceptance. We will notify users of significant updates via email or platform notifications.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Eligibility</h2>
                <p className="text-gray-700 leading-relaxed">
                  You must be at least 18 years old and legally capable of entering into contracts in your jurisdiction.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Governing Law & Dispute Resolution</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  These Terms are governed by the laws of the State of Delaware, without regard to conflicts of law principles.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Any dispute arising from these Terms or the Service shall be resolved exclusively in the state or federal courts located in Delaware County, Delaware.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Contact Information</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  For questions or concerns regarding these Terms:
                </p>
                <ul className="list-none text-gray-700 space-y-2">
                  <li><strong>Email:</strong>{' '}
                    <a href="mailto:support@finance-vitals.com" className="text-blue-600 hover:underline" data-testid="terms-support-email-link">
                      support@finance-vitals.com
                    </a>
                  </li>
                  <li><strong>Website:</strong> finance-vitals.com</li>
                </ul>
              </section>
            </div>

            {/* Footer Navigation */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
                  ← Back to Home
                </Link>
                <div className="space-x-4">
                  <Link to="/privacy" className="text-gray-600 hover:text-gray-800">
                    Privacy Policy
                  </Link>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-500">© 2026 Finance Vitals</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;